import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { MsalAuthenticationTemplate, MsalProvider } from "@azure/msal-react";
import { loginRequest, msalInstance } from "./Services/Authentication/auth_config";
import { InteractionType } from "@azure/msal-browser";
import Useronboard from "./Components/Useronboard";
import MainLayout from "./Layouts/MainLayout";
import MsaPrivateRoute from "./Routes/MsaPrivateRoute";
import PageNotFound from "./CommonComponents/PageNotFound";
import Chat from "./Components/Chat";
import Restricted from "./CommonComponents/Restricted";
import ChatHistory from "./CommonComponents/ChatHistory";
import ErrorComponent from "./CommonComponents/ErrorComponent";
import { Cookies } from 'react-cookie-consent';
import { AbortControllerProvider } from "./Context/AbortControllerContext";
import DynamicComponent from "./Components/StaticComponents/DynamicComponent";
import { setDetectionError, setLowPowerModeStatus, startDetection } from "./Services/Redux/Reducers/deviceSlice";
import { getLowPowerModeStatus, setupLowPowerModeListener } from "./Services/CommonServices/lowPowerModeService";
import { useEffect, useState } from "react";
import { environmetService } from "./Services/ApiServices/environmentServices";
import { useDispatch, useSelector } from "react-redux";
import { AnimatePresence } from "framer-motion";
import BronxTale from "./Components/StaticComponents/BronxTale";
import PlainLayout from "./Layouts/PlainLayout";

function AnimatedRoutes() {
  const dispatch = useDispatch();
    const { isDetectionComplete } = useSelector((state) => state.device);
    const [dynamicPaths, setDynamicPaths] = useState([]);
    const [bucketUrl, setbucketUrl] = useState(Cookies.get('bucketUrl'));
    const [cardFolder, setCardFolder] = useState(Cookies.get('cardFolder'));
    const location = useLocation();
  
    useEffect(() => {
      setbucketUrl(Cookies.get('bucketUrl'));
      setCardFolder(Cookies.get('cardFolder'));
      const fetchPath = async() => {
        await getDynamicPaths();
      }
      fetchPath();
    }, [bucketUrl, cardFolder])
  
    const getDynamicPaths = async () => {
      try {
          // bucketUrl, cardFolder is not defined, skipping dynamic paths fetch
          if (!bucketUrl || !cardFolder) {
            return;
          }
          const response = await fetch(`${bucketUrl}Dynamic_Pages/${cardFolder}/cards.json`)
          if(response.ok){
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              const data = await response.json();
              let a = []
              data.cards.map((x) => {
                a.push(x.path)
                Cookies.set('acceptedPaths', a)
              })
              setDynamicPaths(data.cards);
            } else {
              console.log('Response is not JSON, got content-type:', contentType);
            }
          } else {
            console.log('Failed to fetch dynamic paths. Status:', response.status, 'URL:', `${bucketUrl}Dynamic_Pages/${cardFolder}/cards.json`);
          }
        }
        catch(err){
          console.log('Error occured in fetching dynamic paths. URL attempted:', `${bucketUrl}Dynamic_Pages/${cardFolder}/cards.json`, 'Error:', err.message);
        }
    }
  
    //fetching environment variables and setting it in localstorage
    useEffect(() => {
      console.log('Environment Variables Fetching Initiated', process.env.REACT_APP_APPLICATION_VERSION);
      if (process.env.REACT_APP_APPLICATION_VERSION === "Microsoft") {
        return;
      }
      const getEnvironmentVariables = () => {
        try {
          environmetService(async(response) => { // for env variables 
            if (response && response.data) {            
              localStorage.setItem('storeChat', response.data.storeChat);
              Cookies.set('applicationEnvironment', response.data.applicationEnvironment);
              setbucketUrl(response.data.bucketUrl);
              setCardFolder(response.data.cardFolder);
              Cookies.set('bucketUrl', response.data.bucketUrl);
              Cookies.set('cardFolder', response.data.cardFolder);
              Cookies.set('feedBackCheckMonth', response.data.feedbackmodalindays)
            } else {
              console.log('Invalid response from environment service:', response);
              localStorage.setItem('storeChat', false);
            }
          }, (err) => {
            localStorage.setItem('storeChat', false);
            console.log('Error occured in fetching environment data:', err.message);
          });
        }
        catch(err){
          console.log('Error occured in fetching dynamic paths',err.message);
          localStorage.setItem('storeChat', false);
        }
      }
      getEnvironmentVariables();
    }, [])
  
    // Low Power Mode Detection useEffect
    useEffect(() => {
      const initializeLowPowerModeDetection = async () => {
        try {
          dispatch(startDetection());
          
          // Get initial low power mode status
          const status = await getLowPowerModeStatus();
          dispatch(setLowPowerModeStatus(status));
          
          // Set up listener for changes
          const cleanup = setupLowPowerModeListener((isLowPowerMode) => {
          //  console.log('Low power mode status changed:', isLowPowerMode);
            // Update the status in Redux with the new low power mode value
            const updatedStatus = {
              ...status,
              isLowPowerMode,
              timestamp: Date.now()
            };
            dispatch(setLowPowerModeStatus(updatedStatus));
          });
          
          // Return cleanup function
          return cleanup;
        } catch (error) {
          console.error('Error initializing low power mode detection:', error);
          dispatch(setDetectionError(error.message));
        }
      };
  
      const cleanupPromise = initializeLowPowerModeDetection();
      
      // Cleanup on unmount
      return () => {
        cleanupPromise.then(cleanup => {
          if (cleanup && typeof cleanup === 'function') {
            cleanup();
          }
        });
      };
    }, []);

  return (
    <AnimatePresence mode="wait" initial={false}>
      {isDetectionComplete && (
        <Routes location={location} key={location.pathname}>
          <Route element={<PlainLayout />}>
            <Route 
              path="/" 
              element={
              <MsaPrivateRoute>
                <Useronboard 
                  bucketUrl={bucketUrl}
                  cardFolder={cardFolder}
                />
              </MsaPrivateRoute>
              }
            />
            <Route
              path="/chat"
              element={
                <MsaPrivateRoute>
                  <AbortControllerProvider>
                    <Chat bucketUrl={bucketUrl}/>
                  </AbortControllerProvider>
                </MsaPrivateRoute>
              }
            />
            <Route
              path="/chatHistory"
              element={
                <MsaPrivateRoute>
                  <ChatHistory />
                </MsaPrivateRoute>
              }
            />
            <Route
            path="/bronxtale"
            element={
              <MsaPrivateRoute>
                <BronxTale />
              </MsaPrivateRoute>
            }
          />
          {/* <Route
            path="/polar"
            element={
              <MsaPrivateRoute>
                <PoloBear />
              </MsaPrivateRoute>
            }
          /> */}
          </Route>
          {
            dynamicPaths?.map((path, id) => (
              <Route
                id={"routeId-" + id}
                key={"routeId-" + id}
                exact
                path={path.path}
                element={
                  <MsaPrivateRoute>
                    <DynamicComponent bucketUrl={bucketUrl} cardFolder={cardFolder} />
                  </MsaPrivateRoute>
                }
              />
            ))
          }
          {/* Common Header Layout */}
          {/* <Route element={<MainLayout />}>
          </Route> */}
          <Route path="*" element={<PageNotFound />} />
        </Routes>
      )}
    </AnimatePresence>
  );
}

function MsaApp() {
  // Add CSS animation for loading spinner
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  return (
    <MsalProvider instance={msalInstance}>
      <MsalAuthenticationTemplate 
          interactionType={InteractionType.Redirect} 
          authenticationRequest={loginRequest} 
          errorComponent={ErrorComponent}
      >
        <BrowserRouter>
          <AnimatedRoutes />
        </BrowserRouter>
      </MsalAuthenticationTemplate>
    </MsalProvider>
  );
}

export default MsaApp;